import React from 'react';
import { MapPin, Plane, Mountain, Building } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { coverageAreas } from '../data/mock';

const typeIcons = {
  airport: Plane,
  tour: Mountain,
  city: Building,
  mountain: Mountain
};

const typeColors = {
  airport: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
  tour: 'text-green-400 bg-green-500/10 border-green-500/30',
  city: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  mountain: 'text-purple-400 bg-purple-500/10 border-purple-500/30'
};

const Coverage = () => {
  const { t } = useLanguage();
  
  return (
    <section className="py-20 bg-gray-950 scroll-mt-20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-2 block">
              {t('coverage.badge')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('coverage.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto"></div>
          </div>
          <p className="text-gray-400 text-lg mt-6 max-w-2xl mx-auto">
            {t('coverage.subtitle')}
          </p>
        </div>

        {/* Coverage Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {coverageAreas.map((area, index) => {
            const IconComponent = typeIcons[area.type];
            const colorClass = typeColors[area.type];
            
            return (
              <Card 
                key={area.id}
                className="group bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800 hover:border-amber-500/50 p-6 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-2"
                style={{
                  animationDelay: `${index * 80}ms`
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClass}`}>
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <Badge variant="outline" className="border-gray-700 text-gray-400">
                    {area.duration}
                  </Badge>
                </div>

                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors duration-300">
                  {area.name}
                </h3>

                <div className="flex items-center space-x-2 text-gray-400">
                  <MapPin className="w-4 h-4 text-amber-400" />
                  <span className="text-sm">{area.distance} {t('coverage.fromTurin')}</span>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-lg mb-4">
            {t('coverage.footer1')}
          </p>
          <p className="text-white">
            {t('coverage.footer2')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Coverage;
